import React, { useEffect, useState } from "react";
import {
  Collapse, Empty, Spin, Tooltip,
} from "antd";
import { EyeInvisibleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { isEmpty, toPairs } from "lodash";
import { v4 as uuidv4 } from "uuid";
import SamplesPreview from "./SamplesPreview";
import { getUser } from "../../../utils/helpers";
import { fetchCustomerWos } from "../../../actions/dashboard";
import "./WorkOrders.css";

export default function WorkOrders({
  customerId, dateRange, searchParams, filterRetest,
}) {
  const defaultWo = {
    not_processed: {}, overdue: {}, due_by: {}, available: {},
  };

  const [clickedWo, setClickedWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState(defaultWo);
  const [cards, setCards] = useState([
    { title: "Not Processed", objKey: "not_processed", comptKey: uuidv4() },
    { title: "Due By", objKey: "due_by", comptKey: uuidv4() },
    { title: "Over Due", objKey: "overdue", comptKey: uuidv4() },
  ]);
  const user = getUser();

  const [, setApiController] = useState(null);
  const [isMounted, setIsMounted] = useState(true); // Track mounted state

  const abortPrevApiCall = (controller) => {
    setApiController((prevController) => {
      if (prevController) prevController.abort();
      return controller;
    });
  };

  // Track the component's unmount event
  useEffect(() => () => { setIsMounted(false); }, []); // eslint-disable-line

  const filterHiddenWoBasedOnPermission = (result) => {
    if (user.permissionMap.view_hidden_workorders) return result;

    const newResult = {};
    cards.forEach((card) => {
      const { objKey } = card;
      const newCardObj = {};
      toPairs(result[objKey]).forEach(([date, dateData]) => {
        newCardObj[date] = dateData.filter((wo) => wo.workorder_is_deleted !== 2);
      });
      newResult[objKey] = newCardObj;
    });
    return newResult;
  };

  /**
   * Fetch latest wo data whenever customer id or date range changes
   */
  useEffect(() => {
    const fetchWoData = async () => {
      setLoading(true);
      const controller = new AbortController();
      abortPrevApiCall(controller);
      const payload = { customer_id: customerId, date_range: dateRange, retest: filterRetest };
      if (searchParams.value && searchParams.value !== "") {
        payload[searchParams.paramName] = searchParams.value;
      }
      const {
        success, result, message, canceled,
      } = await fetchCustomerWos(payload, controller.signal);

      if (isMounted) {
        if (success) {
          if (Object.keys(result).length !== 0) {
            setWorkOrders(filterHiddenWoBasedOnPermission(result));
          } else {
            setWorkOrders(defaultWo);
          }
        } else if (!canceled) {
          setWorkOrders(defaultWo);
          toast.error(message);
        }
        // Update the comptKey for force the all Collapse components to rerender
        setCards((prevState) => {
          const newState = prevState.map((state) => ({ ...state, comptKey: uuidv4() }));
          return newState;
        });
        setLoading(canceled === undefined ? false : canceled);
      }
    };

    fetchWoData();
  }, [customerId, dateRange, searchParams, filterRetest]); //eslint-disable-line

  const renderItems = (data) => Object.keys(data).map((date, dateIndex) => ({
    key: (dateIndex + 1).toString(),
    label: date,
    children: (
      <div className="WOCardContainer">
        {data[date].map((workOrder, idx) => (
          <Tooltip
            placement="topLeft"
            key={workOrder.wo_id}
            title={workOrder.wo_overdue_flag === 1 ? "At least one test is past its due date." : ""}
          >
            <div
              className="WOCard"
              onClick={() => setClickedWo(workOrder)}
            >
              {user.permissionMap.view_hidden_workorder_icon_automation && workOrder.workorder_is_deleted === 2 && (<EyeInvisibleOutlined className="WOCard__HiddenIcon" />)}
              {workOrder.wo_overdue_flag === 1 && (
              <>
                <i className="fa fa-clock-o batchWOOverDue" />
              &nbsp;
              </>
              )}
              {`${workOrder.workorder_id}`}
              {idx < data[date].length - 1 && (<strong>,</strong>)}
            </div>
          </Tooltip>
        ))}
      </div>
    ),
  }));

  const renderSection = (card, data) => (
    <div key={card.title}>
      <span className="WorkOrders__Section--title">{card.title}</span>
      {!isEmpty(data) ? (
        <Collapse
          key={card.comptKey}
          items={renderItems(data)}
          expandIconPosition="start"
        />
      ) : (
        <div className="WorkOrders__NoData">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}
    </div>
  );

  return (
    <Spin spinning={loading} size="large">
      <div className="Dashboard__WorkOrders">
        {cards.map((card) => (renderSection(card, workOrders[card.objKey])))}
        {clickedWo && (
          <SamplesPreview
            modalData={{ type: "wo", ...clickedWo }}
            closeModal={() => setClickedWo(null)}
          />
        )}
      </div>
    </Spin>
  );
}
